"use client"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { useChainId, useConfig, useSwitchChain, WagmiContext } from "wagmi"
import { blessnetSepolia } from "../blessnet-chain-configs"
import { VaultProvider } from "../components/useVault"
import { useVaults } from "../hooks"

/**
 * This provider hacks Wagmi into thinking a wallet _is_ connected
 * How:
 * - check if there is a
 */
export const TgWagmiMonkeyProvider = ({
  blessnetAccount,
  children,
}: {
  blessnetAccount: ReturnType<
    typeof useQuery<{
      hashedId: string
      account: `0x${string}`
      exists: boolean
    }>
  >
  children: any
}) => {
  const config = useConfig()
  const chainId = useChainId()
  const { switchChain } = useSwitchChain()

  // this seems a little hacky but it is what it is, at compile time
  // we can't limit chains to only blessnetSepolia...
  useEffect(() => {
    if (chainId !== blessnetSepolia.id) {
      switchChain({ chainId: blessnetSepolia.id })
    }
  }, [chainId, switchChain])

  const state = useMemo(() => {
    if (blessnetAccount.isLoading) {
      return {
        ...config.state,
        status: "connecting" as const,
        current: null,
      }
    }
    if (!blessnetAccount.data?.exists) {
      return {
        ...config.state,
        status: "disconnected" as const,
        current: null,
      }
    }
    config.state.connections.set(blessnetAccount.data.hashedId, {
      chainId,
      accounts: [blessnetAccount.data.account],
      connector: null as any,
    })
    return {
      ...config.state,
      status: "connected" as const,
      current: blessnetAccount.data.hashedId,
    }
  }, [config, chainId, blessnetAccount.data, blessnetAccount.isLoading])

  const wagmiValue = useMemo(
    () => ({
      ...config,
      state,
    }),
    [config, state],
  )

  return (
    <WagmiContext.Provider value={wagmiValue}>
      <VaultProvider>
        {children}
        <BehalfSetter />
      </VaultProvider>
    </WagmiContext.Provider>
  )
}

/**
 * This component is used to set the vault on behalf of the blessed account
 * It needs to be a child of our monkey patched wagmi provider so that
 * it reads the tg account.
 */
const BehalfSetter = () => {
  const { setOnBehalfOf, data, isLoading } = useVaults()
  useEffect(() => {
    if (!data?.asDelegate.length) {
      return
    }
    // atm we only allow one vault per telegram user
    // TODO: allow multiple vaults
    setOnBehalfOf(data.asDelegate[0]!.vault)
  }, [setOnBehalfOf, data, isLoading])
  return null
}
